import axiosInstance from "@/lib/axiosInstance";
import { formBuilderAssignmentsEndpoints } from "./formBuilderAssignments.endpoints";
import type {
  AssignmentClientGroup,
  AssignmentRecord,
  AssignmentTemplateOption,
  AssignmentsTreePagination,
  AssignmentsTreeParams,
  AssignmentsTreeResponse,
  FallbackDefault,
  FormType,
  InheritedDefault,
  VisitCategory,
} from "./formBuilderAssignments.types";

export const FormBuilderAssignmentsQueryKeys = {
  tree: "formBuilderAssignmentsTree",
  options: "formBuilderAssignmentsOptions",
} as const;

function unwrap<T>(payload: unknown): T {
  if (payload != null && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

// Raw shapes from templatesService.getAssignmentsTree(), before the flatter re-pivot below.
interface RawTradeRef {
  id: number;
  name: string;
}

interface RawAssignmentItem {
  id: string;
  formType: string;
  tradeId: number | null;
  trade: RawTradeRef | null;
  subtradeId: number | null;
  subtrade: RawTradeRef | null;
  templateId: string;
  templateName?: string | null;
  meetsRequirements?: boolean;
}

interface RawBrandGroup {
  brandId: string;
  brandName?: string | null;
  assignments?: RawAssignmentItem[];
}

interface RawClientOverrideGroup {
  clientId: string;
  clientName?: string | null;
  overridden?: boolean;
  assignments?: RawAssignmentItem[];
  brands?: RawBrandGroup[];
}

interface RawAssignmentCategory {
  // Backend field is workOrderType; toAssignmentRecord below re-aliases it to
  // the frontend's stable visitCategory vocabulary.
  workOrderType: VisitCategory;
  templateCount?: number;
  overridden?: boolean;
  assignments?: RawAssignmentItem[];
  entityDefaults?: RawAssignmentItem[];
  clientOverrides?: RawClientOverrideGroup[];
  inheritedDefaults?: InheritedDefault[];
}

interface RawClientSummary {
  clientId: string;
  clientName: string | null;
  brandsTotal: number;
  brandIds?: string[];
  brandSummaries?: Array<{ brandId: string; brandName: string | null }>;
}

interface RawAssignmentEntity {
  id?: string;
  entityId?: string;
  name?: string;
  entityName?: string;
  categories?: RawAssignmentCategory[];
  fallbackDefault?: FallbackDefault | null;
  clientsTotal?: number;
  clientSummaries?: RawClientSummary[];
  overrideClientIds?: string[];
}

function toAssignmentRecord(
  item: RawAssignmentItem,
  scope: { visitCategory: VisitCategory; clientId: string | null; brandId: string | null },
): AssignmentRecord {
  return {
    id: item.id,
    visitCategory: scope.visitCategory,
    formType: item.formType as FormType,
    clientId: scope.clientId,
    brandId: scope.brandId,
    tradeId: item.tradeId,
    tradeName: item.trade?.name ?? null,
    subtradeId: item.subtradeId,
    subtradeName: item.subtrade?.name ?? null,
    templateId: item.templateId,
    templateName: item.templateName ?? null,
    meetsRequirements: item.meetsRequirements,
  };
}

// Re-pivots client overrides (nested under each category) into top-level client/brand groups.
function normalizeEntity(raw: RawAssignmentEntity): AssignmentsTreeResponse["entities"][number] {
  const categories = (raw.categories ?? []).map((category) => ({
    visitCategory: category.workOrderType,
    templateCount: category.templateCount ?? 0,
    overridden: category.overridden ?? false,
    inheritedDefaults: category.inheritedDefaults ?? [],
    assignments: (category.assignments ?? category.entityDefaults ?? []).map((item) =>
      toAssignmentRecord(item, {
        visitCategory: category.workOrderType,
        clientId: null,
        brandId: null,
      }),
    ),
  }));

  type BrandAccumulator = AssignmentClientGroup["brands"][number];
  type ClientAccumulator = Omit<
    AssignmentClientGroup,
    "brands" | "brandsTotal" | "overrideBrandIds"
  > & {
    brandsById: Map<string, BrandAccumulator>;
  };

  const clientsById = new Map<string, ClientAccumulator>();
  // Seed in clientSummaries/brandSummaries order: it is the backend's window order (by name)
  // and Map insertion across categories would otherwise reorder clients and brands.
  const summariesById = new Map((raw.clientSummaries ?? []).map((s) => [s.clientId, s]));
  summariesById.forEach((summary) => {
    clientsById.set(summary.clientId, {
      clientId: summary.clientId,
      clientName: summary.clientName ?? "",
      categories: [],
      brandsById: new Map(
        (summary.brandSummaries ?? []).map((brand) => [
          brand.brandId,
          { brandId: brand.brandId, brandName: brand.brandName ?? "", categories: [] },
        ]),
      ),
    });
  });
  (raw.categories ?? []).forEach((category) => {
    (category.clientOverrides ?? []).forEach((override) => {
      const existing: ClientAccumulator = clientsById.get(override.clientId) ?? {
        clientId: override.clientId,
        clientName: override.clientName ?? "",
        categories: [],
        brandsById: new Map(),
      };
      if (!existing.clientName && override.clientName) existing.clientName = override.clientName;
      existing.categories.push({
        visitCategory: category.workOrderType,
        templateCount: (override.assignments ?? []).length,
        overridden: override.overridden ?? true,
        assignments: (override.assignments ?? []).map((item) =>
          toAssignmentRecord(item, {
            visitCategory: category.workOrderType,
            clientId: override.clientId,
            brandId: null,
          }),
        ),
      });
      (override.brands ?? []).forEach((brand) => {
        const existingBrand: BrandAccumulator = existing.brandsById.get(brand.brandId) ?? {
          brandId: brand.brandId,
          brandName: brand.brandName ?? "",
          categories: [],
        };
        if (!existingBrand.brandName && brand.brandName) existingBrand.brandName = brand.brandName;
        existingBrand.categories.push({
          visitCategory: category.workOrderType,
          templateCount: (brand.assignments ?? []).length,
          overridden: true,
          assignments: (brand.assignments ?? []).map((item) =>
            toAssignmentRecord(item, {
              visitCategory: category.workOrderType,
              clientId: override.clientId,
              brandId: brand.brandId,
            }),
          ),
        });
        existing.brandsById.set(brand.brandId, existingBrand);
      });
      clientsById.set(override.clientId, existing);
    });
  });

  const clients = Array.from(clientsById.values()).map(({ brandsById, ...client }) => {
    const brands = Array.from(brandsById.values());
    // Older payloads carry no totals: treat what was sent as everything there is.
    const summary = summariesById.get(client.clientId);
    const brandsTotal = summary?.brandsTotal ?? brands.length;
    return {
      ...client,
      brands,
      brandsTotal,
      ...(summary?.brandIds ? { overrideBrandIds: summary.brandIds } : {}),
    };
  });

  return {
    entityId: raw.entityId ?? raw.id ?? null,
    entityName: raw.entityName ?? raw.name ?? "",
    categories,
    fallbackDefault: raw.fallbackDefault ?? null,
    clients,
    clientsTotal: raw.clientsTotal ?? clients.length,
    ...(raw.overrideClientIds ? { overrideClientIds: raw.overrideClientIds } : {}),
  };
}

export const getAssignmentsTree = async (
  params: AssignmentsTreeParams = {},
  signal?: AbortSignal,
): Promise<AssignmentsTreeResponse> => {
  const query = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ""),
  );
  const response = await axiosInstance.get(formBuilderAssignmentsEndpoints.assignments, {
    params: query,
    signal,
  });
  const data = unwrap<{ entities?: unknown[]; pagination?: AssignmentsTreePagination } | unknown[]>(
    response.data,
  );
  const rawEntities = Array.isArray(data) ? data : (data?.entities ?? []);
  const pagination = Array.isArray(data) ? undefined : data?.pagination;
  return {
    entities: rawEntities.map((entity) => normalizeEntity(entity as RawAssignmentEntity)),
    ...(pagination ? { pagination } : {}),
  };
};

export interface AssignmentOptionsScope {
  workOrderType: VisitCategory;
  tenantId: string;
  clientId?: string;
  brandId?: string;
  tradeId?: number;
  subtradeId?: number;
  scopeLevel: "entity" | "client" | "brand";
}

export const getAssignmentOptions = async (
  scope: AssignmentOptionsScope,
  signal?: AbortSignal,
): Promise<AssignmentTemplateOption[]> => {
  const params = new URLSearchParams({
    workOrderType: scope.workOrderType,
    tenantId: scope.tenantId,
    scopeLevel: scope.scopeLevel,
  });
  if (scope.clientId) params.append("clientId", scope.clientId);
  if (scope.brandId) params.append("brandId", scope.brandId);
  if (scope.tradeId) params.append("tradeId", String(scope.tradeId));
  if (scope.subtradeId) params.append("subtradeId", String(scope.subtradeId));
  const response = await axiosInstance.get(
    `${formBuilderAssignmentsEndpoints.options}?${params.toString()}`,
    { signal },
  );
  return unwrap<AssignmentTemplateOption[]>(response.data) ?? [];
};
